import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormSelect from "../../form-components/SelectComponent";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { Icon } from "../../../components/Component";
import brandMasterApis from "../../../api/master/brandMaster";
import supplierApis from "../../../api/master/supplier";
import debounce from "lodash.debounce";
import { Select } from "antd";
import FormInput from "../../form-components/InputComponent";

interface SelectOptions {
  label: string;
  value: string;
}

const BrandMasterForm: FC<{
  id?: string | null;
  formAction?: string | null;
  toggleModelPopUp: () => void;
  refreshData?: () => void;
}> = ({ toggleModelPopUp, refreshData }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<SelectOptions[]>([]);
  const [materials, setMaterials] = useState<SelectOptions[]>([]);
  const [fetchingMaterials, setFetchingMaterials] = useState(false);
  const [brandId, setBrandId] = useState<string>("");

  const brandByOptions = [
    { label: "Category", value: "Category" },
    { label: "Material", value: "Material" },
  ];

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    register,
    setError,
    clearErrors,
  } = methods;

  const brandBy = watch("brandBy");
  const category = watch("category");
  const makeValue = watch("make");
  const productName = watch("productName");
  const idValue = watch("id");

  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });

  useEffect(() => {
    if (alertInfo.message) {
      const timer = setTimeout(() => {
        setAlertInfo({ type: "", message: "", errors: [] });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [alertInfo.message]);

  const [projectMakeList, setProjectMakeList] = useState<any[]>([]);
  const [makeOptions, setMakeOptions] = useState<SelectOptions[]>([]);
  const [makeSearch, setMakeSearch] = useState("");

  const fetchMakeSuggestions = async (search: string) => {
    setMakeSearch(search);
    if (!search || search.length < 1) {
      setMakeOptions([]);
      return;
    }

    try {
      const simulatedAPIResponse = projectMakeList?.filter((tag) =>
        tag?.name?.toLowerCase()?.includes(search?.toLowerCase())
      );
      const formatted = simulatedAPIResponse?.map((tag) => ({
        label: tag?.name,
        value: tag?.id?.toString(),
      }));

      if (formatted.length === 0 && search.length >= 2) {
        setMakeOptions([
          {
            label: search,
            value: "newMake__" + search,
          },
        ]);
      } else {
        setMakeOptions(formatted);
      }
    } catch (error) {
      console.error("Error fetching make suggestions:", error);
      setMakeOptions([]);
    }
  };

  const debouncedFetch = debounce(fetchMakeSuggestions, 300);

  const fetchBrandsList = async () => {
    try {
      const response: any = await brandMasterApis.fetchBrandDropDown();
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { name: string; id: number }[];
      } = response || {};
      if (status) {
        setProjectMakeList(tagsList);
        const initialOptions = tagsList.map((tag) => ({
          label: tag.name,
          value: tag.id.toString(),
        }));
        setMakeOptions(initialOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await supplierApis.fetchSupplierCategory();
      if (response && response.status) {
        const categoryOptions = response.data.map((category: any) => ({
          label: category.category,
          value: category.id?.toString(),
        }));
        setCategories(categoryOptions);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setAlertInfo({
        type: "error",
        message: "Failed to load categories",
        errors: [],
      });
    }
  };

  const fetchMaterialsByCategory = async (categoryId: string) => {
    if (!categoryId) {
      setMaterials([]);
      return;
    }

    try {
      setFetchingMaterials(true);
      const response = await supplierApis.fetchCategoryProducts(categoryId);
      if (response && response.status) {
        const materialOptions = response.data.map((material: any) => ({
          label: material.materialName,
          value: material.materialId?.toString(),
        }));
        setMaterials(materialOptions);
      } else {
        setMaterials([]);
      }
    } catch (error) {
      console.error("Error fetching materials:", error);
      setMaterials([]);
      setAlertInfo({
        type: "error",
        message: "Failed to load materials for selected category",
        errors: [],
      });
    } finally {
      setFetchingMaterials(false);
    }
  };

  const resetFormFields = () => {
    setValue("category", "");
    setValue("productName", "");
    setValue("make", null);
    setMaterials([]);
    clearErrors(["category", "productName", "make"]);
  };

  const onSubmit = async (data: any) => {
    // console.log("data before submit:", data);

    if (!data.make || !data.make.value) {
      setError("make", {
        type: "required",
        message: "Make is required",
      });
      return;
    }

    try {
      setLoading(true);

      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });

      const payload: any = {
        categoryId: Number(data.category),
      };

      const isNewMake = data.make.value.toString().startsWith("newMake__");
      const makeName = isNewMake
        ? data.make.label || data.make.value.replace("newMake__", "")
        : data.make.label;

      if (brandBy === "Material") {
        const materialIds = Array.isArray(data.productName)
          ? data.productName
            .map((mat: any) => mat.toString?.() || mat.value)
            .join(",")
          : data.productName?.toString() || "";

        payload.materialIds = materialIds;

        if (!isNewMake) {
          payload.makeId = Number(data.make.value);
        } else {
          payload.make = makeName;
        }
      } else if (brandBy === "Category") {
        payload.make = makeName;
      }

      // console.log("Final Payload:", payload);

      const response = await brandMasterApis.createBrand(payload);
      // console.log("API Response:", response);

      if (response.status) {
        // Get the actual brand ID from API response
        const actualBrandId = response.data?.id || response.data?.brandId;
        if (actualBrandId) {
          setBrandId(actualBrandId.toString());
        }

        setAlertInfo({
          type: "success",
          message: "Brand has been created",
          errors: [],
        });

        reset();
        setMakeOptions([]);

        setTimeout(() => {
          if (refreshData) refreshData();
          toggleModelPopUp();
        }, 1500);
      } else {
        setAlertInfo({
          type: "error",
          message: response?.message,
          errors: [],
        });
      }
    } catch (error: any) {
      console.error("Error submitting brand:", error);
      setAlertInfo({
        type: "error",
        message: error.response?.data?.message,
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrandsList();
  }, []);

  useEffect(() => {
    resetFormFields();
  }, [brandBy]);

  useEffect(() => {
    if (brandBy === "Material" && category) {
      fetchMaterialsByCategory(category);
    } else {
      setMaterials([]);
      if (brandBy === "Material") {
        setValue("productName", "");
      }
    }
  }, [brandBy, category, setValue]);

  return (
    <>
      <h5 className="title">Create New Brand</h5>

      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <Icon
              name={
                alertInfo.type === "error" ? "alert-circle" : "check-circle"
              }
            />
            <strong>{alertInfo.message}</strong>
          </Alert>
        </div>
      )}

      <div style={{ marginTop: "15px" }}></div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={6} className="mb-3">
              <FormSelect
                name="brandBy"
                label="Brand By"
                options={brandByOptions}
                required={true}
                placeholder="Select Brand By"
              />
            </Col>

            {brandBy && (
              <Col md={6} className="mb-3">
                <FormSelect
                  name="category"
                  label="Category"
                  options={categories}
                  required={true}
                  placeholder="Select Category"
                />
              </Col>
            )}
            {brandBy === "Material" && (
              <Col md={6} className="mb-3">
                <FormMultiSelect
                  name="productName"
                  label="Material Name"
                  options={materials}
                  required={true}
                  placeholder="Select Material Names"
                  disabled={!category}
                />
              </Col>
            )}

            <Col md="6" className="mb-3">
              <div className="form-group">
                <label className="form-label" htmlFor="make">
                  Make <span className="text-danger">*</span>
                </label>
                <Select
                  showSearch
                  style={{ width: "100%", height: "38px" }}
                  placeholder="Create or Select a make"
                  optionFilterProp="children"
                  value={makeValue}
                  onChange={(value, option) => {
                    setValue(
                      "make",
                      value
                        ? {
                          value,
                        }
                        : null
                    );
                    clearErrors("make");
                  }}
                  onSearch={debouncedFetch}
                  filterOption={false}
                  options={makeOptions}
                  notFoundContent={
                    makeSearch && makeSearch.length < 2
                      ? "Type at least 2 characters"
                      : "No options found"
                  }
                  allowClear
                />
                {errors.make && (
                  <div className="invalid-feedback d-block">
                    {errors.make.message as string}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    <span>Submit</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="light"
                    outline
                    onClick={(ev) => {
                      ev.preventDefault();
                      toggleModelPopUp();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </>
  );
};

export default BrandMasterForm;